/**
 * Centralized tag filtering utilities to ensure consistent behavior
 * across all components in the application.
 */

import { env } from "@/lib/env";

/**
 *
 */
export interface FilterableTag {
  id?: string;
  name: string;
  color?: string;
}

/**
 * Comprehensive filter patterns for unwanted tags
 */
const UNWANTED_TAG_PATTERNS = [
  // Exact matches (case insensitive)
  "agent / na",
  "agent/na",
  "agent na",
  "agent_na",
  "agent-na",

  // Pattern matches - Simplified to avoid catastrophic backtracking
  /^agent[\s/]*n[\s/]*a$/i, // Agent / NA with flexible spacing and slashes (simplified)
  /^agent\s+(?:na|n\/a)$/i, // Agent + NA or N/A (non-capturing group)
  /^(?:na|n\/a)\s*agent$/i, // NA/N/A + Agent (non-capturing group)

  // Generic unwanted patterns - Using non-capturing groups to prevent backtracking
  /^(?:unknown|n\/a|na|none|null|undefined)\s*agent$/i,
  /^agent\s*(?:unknown|n\/a|na|none|null|undefined)$/i,
];

/**
 * Checks if a tag name matches any unwanted patterns
 * @param tagName - The tag name to check
 * @returns true if the tag should be filtered out
 */
export const isUnwantedTag = (tagName: string): boolean => {
  if (!tagName || typeof tagName !== "string") {
    return true; // Filter out null, undefined, or non-string values
  }

  const normalizedName = tagName.trim().toLowerCase();

  // Check exact matches
  if (
    UNWANTED_TAG_PATTERNS.slice(0, 5).some(
      pattern => normalizedName === pattern
    )
  ) {
    return true;
  }

  // Check regex patterns
  const regexPatterns = UNWANTED_TAG_PATTERNS.slice(5);
  return regexPatterns.some(pattern => {
    if (pattern instanceof RegExp) {
      return pattern.test(normalizedName);
    }
    return false;
  });
};

/**
 * Filters out unwanted tags from a list of tag names
 * @param tagNames - Array of tag name strings
 * @returns Filtered array with unwanted tags removed
 */
export const filterUnwantedTagNames = (tagNames: string[]): string[] => {
  if (!Array.isArray(tagNames)) {
    return [];
  }

  return tagNames.filter(tagName => !isUnwantedTag(tagName));
};

/**
 * Filters out unwanted tags from a list of tag objects
 * @param tags - Array of tag objects with name property
 * @returns Filtered array with unwanted tags removed
 */
export const filterUnwantedTags = <T extends FilterableTag>(tags: T[]): T[] => {
  if (!Array.isArray(tags)) {
    return [];
  }

  return tags.filter(tag => !isUnwantedTag(tag.name));
};

/**
 * Validates if a tag name is allowed for creation
 * @param tagName - The tag name to validate
 * @returns Object with validation result and error message if invalid
 */
export const validateTagName = (
  tagName: string
): { isValid: boolean; error?: string } => {
  if (!tagName || typeof tagName !== "string") {
    return { isValid: false, error: "Tag name is required" };
  }

  const trimmedName = tagName.trim();

  if (trimmedName.length === 0) {
    return { isValid: false, error: "Tag name cannot be empty" };
  }

  if (trimmedName.length > 50) {
    return {
      isValid: false,
      error: "Tag name must be less than 50 characters",
    };
  }

  if (isUnwantedTag(trimmedName)) {
    return {
      isValid: false,
      error: "This tag name is not allowed. Please choose a different name.",
    };
  }

  return { isValid: true };
};

/**
 * Debug function to log filtered tags (development only)
 * @param originalTags - Original tag list
 * @param filteredTags - Filtered tag list
 * @param context - Context for logging (component name, etc.)
 */
export const logFilteredTags = <T extends FilterableTag>(
  originalTags: T[],
  filteredTags: T[],
  context: string = "Unknown"
): void => {
  if (
    env.EXPO_PUBLIC_ENV !== "production" &&
    originalTags.length !== filteredTags.length
  ) {
    const removedTags = originalTags.filter(
      originalTag =>
        !filteredTags.some(filteredTag => filteredTag.name === originalTag.name)
    );

    console.log(
      `🚫 [${context}] Filtered out ${removedTags.length} unwanted tags:`,
      removedTags.map(tag => tag.name)
    );
  }
};
